import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar = ({ isTranslator = false, isTomo = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === (isTranslator ? '/translator' : isTomo ? '/tomo' : '/pdf');
  
  // Determine if we're on a translator route
  const isTranslatorRoute = location.pathname.startsWith('/translator');
  const isTomoRoute = location.pathname.startsWith('/tomo');
  const isPdfRoute = location.pathname.startsWith('/pdf');
  
  // Auto-detect route type if not explicitly set
  const actuallyTranslator = isTranslator || isTranslatorRoute;
  const actuallyTomo = isTomo || isTomoRoute;

  return (
    <nav className={`fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 border-b ${actuallyTranslator ? 'border-blue-200' : actuallyTomo ? 'border-orange-200' : 'border-secondary-200'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <motion.div 
            className={`flex items-center space-x-2 ${!isHomePage ? 'cursor-pointer hover:opacity-80 transition-opacity duration-200' : ''}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            onClick={() => !isHomePage && navigate(actuallyTranslator ? '/translator' : actuallyTomo ? '/tomo' : '/pdf')}
          >
            <div className={`w-8 h-8 ${actuallyTranslator ? 'bg-gradient-to-br from-blue-500 to-purple-600' : actuallyTomo ? 'bg-gradient-to-br from-orange-500 to-rose-600' : 'bg-gradient-to-br from-primary-500 to-primary-700'} rounded-lg flex items-center justify-center`}>
              {actuallyTranslator ? (
                <img 
                  src={process.env.PUBLIC_URL + '/favicon_translator.png'} 
                  alt="Quick Translator" 
                  className="w-5 h-5 rounded"
                  onError={(e) => {
                    console.log('Image failed to load:', e.target.src);
                    e.target.style.display = 'none';
                  }}
                />
              ) : actuallyTomo ? (
                <img 
                  src={process.env.PUBLIC_URL + '/favicon_tomo.png'}
                  alt="Tomo" 
                  className="w-5 h-5 rounded"
                  onError={(e) => {
                    console.log('Image failed to load:', e.target.src);
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <img 
                  src={process.env.PUBLIC_URL + '/favicon.png'} 
                  alt="PDF Viewer" 
                  className="w-5 h-5 rounded"
                  onError={(e) => {
                    console.log('Image failed to load:', e.target.src);
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </div>
            <span className={`text-xl font-bold ${actuallyTranslator ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600' : actuallyTomo ? 'text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-rose-600' : 'text-gradient'}`}>
              {actuallyTranslator ? 'Quick Translator' : actuallyTomo ? 'Tomo' : 'PDF Viewer'}
            </span>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className={`${actuallyTranslator ? 'text-gray-600 hover:text-blue-600' : actuallyTomo ? 'text-gray-600 hover:text-orange-600' : 'text-secondary-600 hover:text-primary-600'} transition-colors duration-200 font-medium`}>
              Features
            </a>
            {!actuallyTranslator && (
              <a href="#demo" className="text-secondary-600 hover:text-primary-600 transition-colors duration-200 font-medium">
                Demo
              </a>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`${actuallyTranslator ? 'text-gray-600 hover:text-blue-600' : actuallyTomo ? 'text-gray-600 hover:text-orange-600' : 'text-secondary-600 hover:text-primary-600'} transition-colors duration-200`}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div 
            className="md:hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={`px-2 pt-2 pb-3 space-y-1 bg-white border-t ${actuallyTranslator ? 'border-blue-200' : actuallyTomo ? 'border-orange-200' : 'border-secondary-200'}`}>
                              <a href="#features" className={`block px-3 py-2 ${actuallyTranslator ? 'text-gray-600 hover:text-blue-600' : actuallyTomo ? 'text-gray-600 hover:text-orange-600' : 'text-secondary-600 hover:text-primary-600'} transition-colors duration-200 font-medium`}>
                Features
              </a>
              {!actuallyTranslator && (
                <>
                  <a href="#demo" className="block px-3 py-2 text-secondary-600 hover:text-primary-600 transition-colors duration-200 font-medium">
                    Demo
                  </a>
                  <a href="#download" className="block px-3 py-2 btn-primary text-center">
                    Download
                  </a>
                </>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navbar; 